import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { generalFollowActions } from 'src/store/slices/generalFollowSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    fontFamily: 'Segoe UI'
  },
  mainCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '16px',
    position: 'sticky',
    top: 210,
    zIndex: 7,
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: '16px'
  },
  cardHeader: {
    backgroundColor: '#f5f5f5',
    color: '#424242',
    padding: '10px 16px',
    cursor: 'move',
    borderBottom: '1px solid #e0e0e0',
    '& .MuiCardHeader-title': {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.5px'
    }
  },
  div: {
    fontWeight: 'bold',
    fontSize: '14px',
    width: '100%',
    color: 'red'
  }
}));

const NumberFormatCustom = React.forwardRef(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value || 0
            }
          });
        }}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={2}
        allowNegative={false}
        isNumericString
        suffix="₺"
      />
    );
  }
);

function Refund() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const invoiceData = useSelector(
    (state) => state.generalFollowSlice.invoiceDetail
  );

  const [expanded, setExpanded] = React.useState(true);
  const [bankCode, setBankCode] = React.useState(
    invoiceData[0]?.Exchange?.BankCode
  );
  const [bankName, setBankName] = React.useState(
    invoiceData[0]?.Exchange?.BankName
  );
  const [ibanNo, setIbanNo] = React.useState(invoiceData[0]?.Exchange?.IBANNO);
  const [webSalesInvoiceNumber, setWebSalesInvoiceNumber] = React.useState(
    invoiceData[0]?.Exchange?.WebSalesInvoiceNumber
  );
  const ibanNumber = useSelector(
    (state) => state.generalFollowSlice.invoiceDetail
  )[0]?.Exchange?.IBANNO;

  const [subModelDifferenceAmount, setSubModelDifferenceAmount] =
    React.useState(invoiceData[0]?.Exchange?.SubModelDifferenceAmount);

  const [isIbanValid, setIsIbanValid] = React.useState(null);

  const TRiban = ibanNumber?.substring(2);

  const handleToggleCollapse = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (ibanNo?.length > 2) {
      const isValidIban = IbanValidator(invoiceData[0]?.Exchange?.IBANNO);
      setIsIbanValid(isValidIban);

      const result = isValidIban ? 1 : 0;
      dispatch(generalFollowActions.updateIsIbanNoValid(result));
    }
  }, []);

  function changeBankCode(val) {
    //Banka kodu
    var tempData = JSON.parse(JSON.stringify(invoiceData));
    tempData[0].Exchange.BankCode = val;
    dispatch(generalFollowActions.updateNewData(tempData));
    setBankCode(val);
  }

  function changeBankName(val) {
    //Banka Adı
    var tempData = JSON.parse(JSON.stringify(invoiceData));
    tempData[0].Exchange.BankName = val;
    dispatch(generalFollowActions.updateNewData(tempData));
    setBankName(val);
  }

  function IbanValidator(iban) {
    if (iban.length !== 26 || !iban.startsWith('TR')) {
      return false;
    }
    // Mod 97 kontrolü
    const reformattedIBAN = iban.slice(4) + iban.slice(0, 4); // İlk 4 karakteri sona taşı
    // Harfleri sayılara çevir
    const numericIBAN = reformattedIBAN.replace(
      /[A-Z]/g,
      (char) => char.charCodeAt(0) - 55
    );

    let remainder = 0;
    for (let i = 0; i < numericIBAN.length; i++) {
      remainder = (remainder * 10 + parseInt(numericIBAN[i], 10)) % 97; // Mod işlemi
    }

    // Mod 97 sonucu 1 olmalı
    return remainder === 1;
  }

  function handleChangeBankNo(event) {
    const inputValue = event.target.value.toUpperCase();
    const sanitizedValue = inputValue.replace(/TR/g, '');

    let finalIban = '';

    if (sanitizedValue.trim() !== '') {
      // Kullanıcı alanı boş değilse IBAN'ı oluştur
      finalIban = sanitizedValue.startsWith('TR')
        ? `TR${sanitizedValue.slice(2)}` // Zaten TR varsa, bir kere al
        : `TR${sanitizedValue}`; // TR yoksa ekle
    }

    const ibanToSet = finalIban.slice(0, 26);

    // IBAN doğrulama işlemi
    const isValid = ibanToSet.length > 2 && IbanValidator(ibanToSet);
    setIsIbanValid(isValid);

    const result = isValid ? 1 : 0;
    dispatch(generalFollowActions.updateIsIbanNoValid(result));

    const tempData = JSON.parse(JSON.stringify(invoiceData));
    tempData[0].Exchange.IBANNO = ibanToSet || ''; // Alan boşsa boş bir değer gönder
    dispatch(generalFollowActions.updateNewData(tempData));

    setIbanNo(ibanToSet);
  }

  function changeWebSalesInvoiceNumber(val) {
    //Web Satış Fatura Numarası
    var tempData = JSON.parse(JSON.stringify(invoiceData));
    tempData[0].Exchange.WebSalesInvoiceNumber = val;
    dispatch(generalFollowActions.updateNewData(tempData));
    setWebSalesInvoiceNumber(val);
  }

  function changeSubModelDifferenceAmount(val) {
    dispatch(generalFollowActions.updateSubModelDifferenceAmount(val));
    //Alt Model Fark Tutarı
    var tempData = JSON.parse(JSON.stringify(invoiceData));
    tempData[0].Exchange.SubModelDifferenceAmount = val;
    dispatch(generalFollowActions.updateNewData(tempData));
    setSubModelDifferenceAmount(val);
  }
  return (
    <>
      <Card className={classes.mainCard}>
        <CardHeader
          className={classes.cardHeader}
          avatar={<DragIndicatorIcon sx={{ color: '#FF9800' }} />}
          action={
            <Tooltip title={expanded ? 'Daralt' : 'Genişlet'}>
              <IconButton
                onClick={handleToggleCollapse}
                sx={{ color: '#FF9800' }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          }
          title="Banka İşlemleri"
        />
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ padding: '12px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Tooltip title="Banka Adı">
                  <TextField
                    id="input-with-icon-textfield"
                    label="Banka Adı"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={bankName}
                    onChange={(event) => {
                      changeBankName(event.target.value);
                    }}
                    autoComplete="off"
                    inputProps={{
                      style: { fontSize: '12px' }
                    }}
                    InputLabelProps={{
                      style: { fontSize: '11px', color: '#757575' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <InputMask
                  mask="TR**************************"
                  value={ibanNo}
                  maskChar={null}
                  onChange={handleChangeBankNo}
                >
                  {() => (
                    <TextField
                      id="input-with-icon-textfield"
                      label="IBAN No"
                      size="small"
                      fullWidth
                      autoComplete="off"
                      variant="outlined"
                      error={!isIbanValid && ibanNo.length > 2}
                      helperText={
                        ibanNo.length > 2
                          ? isIbanValid
                            ? 'IBAN numarası geçerlidir.'
                            : 'Geçersiz IBAN numarası. Lütfen kontrol edin.'
                          : ''
                      }
                      InputProps={{
                        endAdornment: isIbanValid ? (
                          <CheckCircleIcon style={{ color: 'green' }} />
                        ) : (
                          ibanNo.length > 2 && (
                            <CancelIcon style={{ color: 'red' }} />
                          )
                        ),
                        style: { fontSize: '12px' }
                      }}
                      InputLabelProps={{
                        style: { fontSize: '11px', color: '#757575' }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: isIbanValid ? 'green' : 'default'
                          },
                          '&:hover fieldset': {
                            borderColor: isIbanValid ? 'darkgreen' : 'default'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isIbanValid ? 'green' : 'default'
                          }
                        }
                      }}
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12}>
                <Tooltip title="Web Satış Fatura Numarası">
                  <TextField
                    id="input-with-icon-textfield"
                    label="Web Satış Fatura Numarası"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={webSalesInvoiceNumber}
                    onChange={(event) => {
                      changeWebSalesInvoiceNumber(event.target.value);
                    }}
                    autoComplete="off"
                    inputProps={{
                      style: { fontSize: '12px' }
                    }}
                    InputLabelProps={{
                      style: { fontSize: '11px', color: '#757575' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Tooltip title="Alt Model Fark Tutarı">
                  <TextField
                    label="Alt Model Fark Tutarı"
                    name="numberformat"
                    id="formatted-numberformat-input"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={subModelDifferenceAmount}
                    onChange={(event) => {
                      changeSubModelDifferenceAmount(event.target.value);
                    }}
                    autoComplete="off"
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      style: { fontSize: '12px' }
                    }}
                    InputLabelProps={{
                      style: { fontSize: '11px', color: '#757575' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                    }}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>
    </>
  );
}

export default Refund;
